import { useState } from "react";
import { toast } from "react-toastify";

import CustomFetching from "@/components/desktop/fetching/CustomFetching";
import CustomModalForm from "@/components/desktop/form/modal/CustomModalForm/CustomModalForm";
import CustomButton from "@/components/desktop/button/CustomButton";
import { formatNumber } from "@/utils/numberFormat";
import { formatDateTime } from "@/utils/dateFormatter";

import { useIOAssociations, useConfirmIOAssociation } from "@/hooks/histories/useIOAssociation";

import styles from "./io-associate.module.scss";

const INITIAL_PAGINATION = { page: 1, paginate: 10 };

const UNIT_LABEL = {
	cc: "L",
	u: "Un",
};

const getUnitLabel = (unitMeasurement) => UNIT_LABEL[unitMeasurement] || "Kg";

const parseDecimal = (value) => {
	if (!value) return 0;
	return Number.parseFloat(String(value).replace(",", ".")) || 0;
};

const IOAssociate = ({ isOpen, applicationHistoryId, listType = null, filters = null, onClose }) => {
	const [pagination, setPagination] = useState({ ...INITIAL_PAGINATION });
	const [associations, setAssociations] = useState([]);
	const [quantityMap, setQuantityMap] = useState({});

	const queryParams = isOpen ? { ...pagination, application_history_id: applicationHistoryId } : null;

	const { data: responseData, isLoading } = useIOAssociations(queryParams, {
		enabled: isOpen && !!applicationHistoryId,
		onSuccess: (response) => {
			const pageData = response?.list?.data || [];
			setAssociations(
				pageData.map((item) => ({
					...item,
					product_weight_assoc: quantityMap[item.applications_history_id] ?? item.product_weight_assoc,
				})),
			);
		},
		onError: () => toast.error("Error al cargar las asociaciones"),
	});

	const confirmMutation = useConfirmIOAssociation(listType, filters);

	const applicationInfo = responseData?.application;
	const currentPage = responseData?.list;
	const unitLabel = getUnitLabel(applicationInfo?.unit_measurement);
	const quantityToDistribute = parseDecimal(applicationInfo?.quantity_to_distribute);
	const availableQuantity = quantityToDistribute;

	const getTotalEntered = () => associations.reduce((sum, association) => sum + parseDecimal(association.product_weight_assoc), 0);

	const getMaxForRow = (index) => {
		const rowCapacity = parseDecimal(associations[index]?.product_weight);
		const otherRowsTotal = associations.reduce((sum, association, i) => {
			if (i === index) return sum;
			return sum + parseDecimal(association.product_weight_assoc);
		}, 0);
		return Math.min(rowCapacity, Math.max(0, availableQuantity - otherRowsTotal));
	};

	const handleAssociationQuantity = (index, value) => {
		const numericValue = parseDecimal(value);
		const effectiveMax = getMaxForRow(index);
		const clampedValue = Math.min(numericValue, effectiveMax);
		const associationId = associations[index]?.applications_history_id;

		setQuantityMap((previous) => ({ ...previous, [associationId]: clampedValue }));
		setAssociations((previous) => {
			const updated = [...previous];
			updated[index] = { ...updated[index], product_weight_assoc: clampedValue };
			return updated;
		});
	};

	const handleClose = () => onClose();

	const handleConfirm = async () => {
		if (!associations?.length) {
			toast.error("No es posible generar la asociación. Comuníquese con administración.");
			return;
		}

		let exceedsQuantity = false;

		for (const association of associations) {
			const quantity = parseDecimal(association.product_weight_assoc);
			if (quantity > parseDecimal(association.product_weight)) {
				exceedsQuantity = true;
				break;
			}
		}

		if (exceedsQuantity) {
			toast.warning("El contenido ingresado no puede superar la cantidad utilizada");
			return;
		}

		const totalEntered = getTotalEntered();

		if (totalEntered === 0) {
			toast.warning("Debe ingresar al menos una cantidad para asociar");
			return;
		}

		if (availableQuantity < totalEntered) {
			toast.warning("Debe ingresar una cantidad menor o igual a lo disponible para asociar");
			return;
		}

		const filteredAssociations = associations.filter((association) => parseDecimal(association.product_weight_assoc) !== 0);

		try {
			await confirmMutation.mutateAsync({
				application_history_id: Number(applicationHistoryId),
				associations: filteredAssociations,
				list_type: listType,
				filters,
			});
			toast.success("Aplicación asociada correctamente");
			handleClose();
		} catch {
			toast.error("Error al asociar las aplicaciones");
		}
	};

	if (!isOpen) {
		return null;
	}

	return (
		<CustomModalForm
			open={isOpen}
			title="Asociar devolución de producto genérico"
			onClose={handleClose}
			showSave={false}
			size="xl"
			gridTable={false}
			isLoading={confirmMutation.isLoading}
			processingMessage="Asociando aplicaciones de IO..."
			footer={
				<>
					<CustomButton variant="secondary" onClick={handleClose} disabled={confirmMutation.isLoading}>
						Cerrar
					</CustomButton>
					<CustomButton variant="primary" onClick={handleConfirm} loading={confirmMutation.isLoading} disabled={isLoading}>
						Confirmar
					</CustomButton>
				</>
			}
		>
			{isLoading ? (
				<CustomFetching center message="Cargando aplicaciones..." />
			) : (
				<div className={styles.container}>
					{applicationInfo && (
						<section className={styles.heroCard}>
							<div className={styles.heroBadge}>Asociar devolución</div>
							<h3 className={styles.heroTitle}>
								({applicationInfo?.inventory_code}) {applicationInfo?.product_name}
							</h3>
							<div className={styles.heroMetaGrid}>
								<div className={styles.heroMetaItem}>
									<span className={styles.heroMetaLabel}>Cantidad para distribuir</span>
									<strong className={styles.heroMetaValue}>
										{formatNumber(quantityToDistribute, 2)} {unitLabel}
									</strong>
								</div>
								<div className={styles.heroMetaItem}>
									<span className={styles.heroMetaLabel}>Disponible para asociar</span>
									<strong className={styles.heroMetaValue}>
										{formatNumber(availableQuantity, 2)} {unitLabel}
									</strong>
								</div>
							</div>
						</section>
					)}

					<section className={styles.tableCard}>
						<table className={styles.table}>
							<thead>
								<tr className={styles.headerRow}>
									<th className={styles.headerCellLeft}>Fecha</th>
									<th className={styles.headerCellRight}>Cant. Utilizada</th>
									<th className={styles.headerCellRight}>Cant. Asociar</th>
								</tr>
							</thead>
							<tbody>
								{associations.map((association, index) => (
									<tr key={index} className={styles.bodyRow}>
										<td className={styles.cellLeft}>{formatDateTime(association.date_association) || "-"}</td>
										<td className={styles.cellRight}>
											{formatNumber(parseDecimal(association.product_weight), 2)} {association.unit_measurement}
										</td>
										<td className={styles.cellRight}>
											<input
												type="number"
												min={0}
												max={getMaxForRow(index)}
												step="0.01"
												value={association.product_weight_assoc || ""}
												onChange={(event) => handleAssociationQuantity(index, event.target.value)}
												className={styles.quantityInput}
											/>
										</td>
									</tr>
								))}

								{associations.length === 0 && (
									<tr>
										<td colSpan={3} className={styles.emptyRow}>
											No hay aplicaciones para asociar
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</section>

					{currentPage && (
						<div className={styles.paginationRow}>
							<CustomButton
								size="sm"
								variant="neutral"
								disabled={pagination.page <= 1}
								onClick={() => setPagination((previous) => ({ ...previous, page: previous.page - 1 }))}
							>
								Anterior
							</CustomButton>
							<span className={styles.paginationText}>
								Pág. {pagination.page} de {currentPage.last_page} ({currentPage.total} registros)
							</span>
							<CustomButton
								size="sm"
								variant="neutral"
								disabled={pagination.page >= currentPage.last_page}
								onClick={() => setPagination((previous) => ({ ...previous, page: previous.page + 1 }))}
							>
								Siguiente
							</CustomButton>
						</div>
					)}
				</div>
			)}
		</CustomModalForm>
	);
};

export default IOAssociate;
